import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { resetAppointment as resetAppointmentAction } from 'actions';
import {
  clearServiceCategory as clearServiceCategoryAction,
  selectServiceCategory as selectServiceCategoryAction,
} from 'actions/SelectedServiceCategoryActions';
import {
  clearServices as clearServicesAction,
  hideMaxAppointmentsWarning as hideMaxAppointmentsWarningAction,
  selectService as selectServiceAction,
  unselectService as unselectServiceAction,
} from 'actions/SelectedServicesActions';
import { trackEvent } from 'integrations/metrics';
import { EventNames } from 'integrations/metrics/events';
import { EventAction, EventObjectType } from 'integrations/metrics/types';
import { getMaxAppointmentsWarning } from 'selectors/bookableAppointments';
import { getSelectedProfessional } from 'selectors/professionals';
import {
  getMapServiceToSelectedOption,
  getSelectableServiceCategories,
  getSelectableServices,
  getSelectedServiceCategory,
  getSelectedServices,
  getServicesToBook,
} from 'selectors/services';

import { TService, TServiceOption } from 'types/service';

export const useServices = () => {
  const services: TService[] = useSelector(getSelectableServices);
  const selectedServices = useSelector(getSelectedServices);
  const selectedServicesOrOptions = useSelector(getServicesToBook);
  const mapServiceToSelectedOption = useSelector(getMapServiceToSelectedOption);
  const serviceCategories = useSelector(getSelectableServiceCategories);
  const selectedServiceCategory = useSelector(getSelectedServiceCategory);
  const selectedProfessional = useSelector(getSelectedProfessional);
  const maxAppointmentsWarning = useSelector(getMaxAppointmentsWarning);

  const dispatch = useDispatch();
  // Dispatch handlers
  const selectServiceCategory = (serviceCategory: string | null) =>
    dispatch(selectServiceCategoryAction(serviceCategory));
  const clearServices = () => dispatch(clearServicesAction());
  const selectService = (
    guid: string,
    providerGuid?: string,
    optionId?: string,
    bookingOrder?: number,
  ) =>
    dispatch(selectServiceAction(guid, providerGuid, optionId, bookingOrder));
  const unselectService = (guid: string, optionId?: string) =>
    dispatch(unselectServiceAction(guid, optionId));
  const clearServiceCategory = () => dispatch(clearServiceCategoryAction());
  const hideMaxAppointmentsWarning = (triggeringServiceGuid?: string) =>
    dispatch(hideMaxAppointmentsWarningAction(triggeringServiceGuid));

  const resetAppointment = () => dispatch(resetAppointmentAction());

  const toggleService = (
    id: string,
    optionId?: string,
    bookingOder?: number,
  ) => {
    if (selectedServices?.includes(id)) {
      if (selectedServices?.length === 1) {
        resetAppointment();
      }

      trackEvent(EventNames.SELECT_BUTTON_CLICKED, {
        action: EventAction.SELECT,
        productSection: 'Booking',
        productSubSection: 'Services',
        objectType: EventObjectType.BUTTON,
        objectValue: 'Unselect',
        service_added: false,
        service_id: id,
        option_id: optionId || mapServiceToSelectedOption[id],
        service_ids: selectedServices,
      });

      unselectService(id, optionId || mapServiceToSelectedOption[id]);
    } else {
      trackEvent(EventNames.SELECT_BUTTON_CLICKED, {
        action: EventAction.SELECT,
        productSection: 'Booking',
        productSubSection: 'Services',
        objectType: EventObjectType.BUTTON,
        objectValue: 'Select',
        service_added: true,
        service_id: id,
        option_id: optionId,
        service_ids: selectedServices,
      });
      selectService(id, selectedProfessional?.guid, optionId, bookingOder);
    }
  };

  const getSelectedOption = React.useCallback(
    (service: TService): TServiceOption | undefined => {
      const selectedOptionID = mapServiceToSelectedOption[service.guid];

      if (selectedOptionID) {
        return service.options?.find(
          (option) => option.guid === selectedOptionID,
        );
      }
    },
    [mapServiceToSelectedOption],
  );

  const onCloseMaxAppointmentsWarning = () => {
    const triggeringGuid = maxAppointmentsWarning.triggeringServiceGuid;

    // Check if triggeringGuid is an optionId or a base service GUID
    const isOptionId = !selectedServices.includes(triggeringGuid);

    if (isOptionId) {
      // triggeringGuid is an optionId, find the base service GUID
      const serviceGuid = Object.keys(mapServiceToSelectedOption).find(
        (serviceId) => mapServiceToSelectedOption[serviceId] === triggeringGuid,
      );

      if (serviceGuid) {
        unselectService(serviceGuid, triggeringGuid);
      }
    } else {
      // triggeringGuid is a base service GUID
      const optionId = mapServiceToSelectedOption[triggeringGuid];

      unselectService(triggeringGuid, optionId);
    }

    hideMaxAppointmentsWarning(triggeringGuid);
  };

  return {
    services,
    selectedServices,
    selectedServicesOrOptions,
    serviceCategories,
    showMaxAppointmentsWarning: maxAppointmentsWarning?.show,
    getSelectedOption,
    selectedServiceCategory,
    toggleService,
    selectService,
    unselectService,
    selectServiceCategory,
    clearServices,
    clearServiceCategory,
    onCloseMaxAppointmentsWarning,
  };
};
